import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Avatar, Button, Callout, Field, Skeleton } from '../components/ui.tsx';
import { ApiError, get, post } from '../lib/api.ts';
import { AuthCanvas } from './Login.tsx';

interface Details {
  uid: string;
  prompt: 'login' | 'consent';
  scopes: string[];
  client: { id: string; name: string; firstParty: boolean };
  signedInAs: { name: string; email: string } | null;
}

const SCOPE_TEXT: Record<string, string> = {
  openid: 'Confirm it’s you',
  profile: 'Your name',
  email: 'Your work email',
  employee: 'Your office, division and position',
  rbac: 'Your roles in GX apps',
  offline_access: 'Stay signed in',
};

export function Interaction() {
  const { uid } = useParams({ strict: false }) as { uid: string };
  const details = useQuery({ queryKey: ['interaction', uid], queryFn: () => get<Details>(`/auth/interaction/${uid}/details`), retry: false });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [other, setOther] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async (path: string, body?: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ redirectTo: string }>(`/auth/interaction/${uid}/${path}`, body ?? {});
      window.location.assign(r.redirectTo);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
      setBusy(false);
    }
  };

  const d = details.data;
  return (
    <AuthCanvas statement={d ? <>Continue to <em>{d.client.name}</em>.</> : undefined}>
      <div className="auth__form-inner">
        {details.isLoading ? (
          <div className="stack">
            <Skeleton h={12} w={90} />
            <Skeleton h={48} />
            <Skeleton h={42} />
          </div>
        ) : details.isError || !d ? (
          <Callout tone="bad" title="This sign-in link has expired.">
            Go back to the app you were using and start again.
          </Callout>
        ) : d.prompt === 'consent' ? (
          <div className="stack">
            <span className="eyebrow">Permission</span>
            <h1 className="display" style={{ fontSize: 'var(--step-4)' }}>
              {d.client.name} <em>would like</em>
            </h1>
            <ul className="list-plain">
              {d.scopes.map((s) => (
                <li key={s} className="mini-row">
                  <span className="chip chip--accent chip--plain">{s}</span>
                  {SCOPE_TEXT[s] ?? s}
                </li>
              ))}
            </ul>
            {error ? <Callout tone="bad">{error}</Callout> : null}
            <div className="row">
              <Button variant="primary" size="lg" loading={busy} onClick={() => go('confirm')}>
                Allow
              </Button>
              <Button variant="ghost" size="lg" disabled={busy} onClick={() => go('abort')}>
                Cancel
              </Button>
            </div>
          </div>
        ) : d.signedInAs && !other ? (
          <div className="stack">
            <span className="eyebrow">Sign in to {d.client.name}</span>
            <h1 className="display" style={{ fontSize: 'var(--step-4)' }}>
              Continue as <em>{d.signedInAs.name.split(' ')[0]}?</em>
            </h1>
            <div className="me-card">
              <Avatar name={d.signedInAs.name} />
              <div>
                <div className="me-card__name">{d.signedInAs.name}</div>
                <div className="me-card__role">{d.signedInAs.email}</div>
              </div>
            </div>
            {error ? <Callout tone="bad">{error}</Callout> : null}
            <Button variant="primary" size="lg" loading={busy} iconRight="arrowRight" onClick={() => go('login')}>
              Continue
            </Button>
            <Button variant="ghost" onClick={() => setOther(true)}>
              Use another account
            </Button>
          </div>
        ) : (
          <form
            className="stack"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              void go('login', { email, password });
            }}
          >
            <span className="eyebrow">Sign in to {d.client.name}</span>
            <h1 className="display" style={{ fontSize: 'var(--step-4)' }}>
              One account, <em>every app.</em>
            </h1>
            {error ? <Callout tone="bad">{error}</Callout> : null}
            <Field label="Work email">{(p) => <input {...p} className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
            <Field label="Password">{(p) => <input {...p} className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
            <Button variant="primary" size="lg" type="submit" loading={busy} iconRight="arrowRight">
              Sign in
            </Button>
            <Button variant="ghost" type="button" disabled={busy} onClick={() => go('abort')}>
              Cancel and return
            </Button>
          </form>
        )}
      </div>
    </AuthCanvas>
  );
}
